import BankIcon from '@components/v2/BankIcon';
import Select from '@components/v2/Select';
import { Lock } from '@huspy/briks-icons';
import {
  Box, Flex, Text
} from '@huspy/briks-web';
import { HStack, Stack } from '@huspy/styled-system/jsx';
import { BankMultiSelect } from '@modules/banks/features/BankMultiSelect';
import { useGetBanks } from '@modules/banks/hooks/queries/useGetBanks';
import { MortgageType } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { getMaxBankCount, getMortgageType } from '@modules/opportunities/presentation/pages/const';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Bank = {
  name: string;
  icon: string;
  id: string;
  error: any;
  mortgageType: MortgageType;
  isSubmitted: boolean;
};
type SelectBankMortgageTypeCardProps = {
  banks: Bank[];
  onMortgageTypeChange(idx: number, value: MortgageType): void;
};

const baseTranslation = 'clients.formLabels';

const SingleMortgageTypeSelector = ({
  mortgageType,
  onChange,
}: {
  mortgageType: MortgageType;
  onChange: () => void;
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [onChange]);

  return (
    <Select
      size='lg'
      data={ getMortgageType() }
      placeholder={ t(`${baseTranslation}.selectMortgageType`) }
      defaultValue={ [mortgageType] }
      value={ [mortgageType] }
      disabled
    />
  );
};

const SubmittedBank = ({ mortgageType }:{ mortgageType: MortgageType }) => (
  <HStack gap='3.5' justifyContent='center'>
    <Text size='lg' weight='medium' color='text-tertiary'>
      {getMortgageType().find((mt) => mt.value === mortgageType)?.label}
    </Text>
    <Box color='fg-tertiary'>
      <Lock color='inherit' height='20px' width='20px' />
    </Box>
  </HStack>
);

const SelectBankMortgageTypeCard = ({ banks, onMortgageTypeChange }: SelectBankMortgageTypeCardProps) => {
  const { t } = useTranslation();
  const { data } = useGetBanks();

  const componentToRender = (
    {
      id, mortgageType, error, isSubmitted,
    }: Bank,
    idx: number
  ) => {
    const supportedMortgageTypes = data?.find((item) => item.external_id === id)?.supported_mortgage_types ?? [];
    const hasSingleMortgageType = supportedMortgageTypes.length === 1;
    if (isSubmitted) {
      return (
        <SubmittedBank mortgageType={ mortgageType } />
      );
    }
    if (hasSingleMortgageType) {
      return (
        <SingleMortgageTypeSelector
          mortgageType={ supportedMortgageTypes[0]! }
          onChange={ () => onMortgageTypeChange(idx, supportedMortgageTypes[0]!) }
        />
      );
    }
    return (
      <Box
        id={ `selected_banks.${idx}.mortgageType` }
        w='100%'
      >
        <Select
          size='lg'
          name={ id }
          data={ getMortgageType() }
          placeholder={ t(`${baseTranslation}.selectMortgageType`) }
          onValueChange={ (e) => {
            onMortgageTypeChange(idx, e.value[0] as MortgageType);
          } }
          isError={ !!error }
          defaultValue={ [mortgageType] }
          value={ [mortgageType] }
        />
      </Box>
    );
  };
  return (
    <Stack gap='1.5'>
      <Text size='md' weight='medium' color='text-secondary'>
        {
          t(`${baseTranslation}.selectMortgageType`)
        }
      </Text>
      <Stack gap='3'>
        {
          banks.map((bank, idx) => (
            <Flex
              key={ bank.id }
              justifyContent='space-between'
              bg='white'
              borderRadius='radius-3'
              py='3'
              px='2.5'
              borderColor='border-secondary'
              borderWidth='1px'
            >
              <HStack gap='2' flexBasis='50%'>
                <BankIcon w='40px' h='40px' icon={ bank.icon } hasShadow={ false } />
                <Text size='lg' weight='medium'>{bank.name}</Text>
              </HStack>
              {componentToRender(bank, idx)}
            </Flex>
          ))
        }
      </Stack>
    </Stack>
  );
};

type SelectedBanks = (NonNullable<
React.ComponentProps<typeof BankMultiSelect>['defaultValue']
>[number] & {
  mortgageType: MortgageType | undefined;
})[];

type Props = {
  onChange(selected_banks: SelectedBanks): void;
  error: any;
  defaultValue: SelectedBanks;
  isOpen?: boolean;
  banks?: SelectBankMortgageTypeCardProps['banks'];
  onMortgageTypeChange?: (idx: number, value: MortgageType) => void;
  defaultMortgageType?: MortgageType;
} & (
  { banks: SelectBankMortgageTypeCardProps['banks']; onMortgageTypeChange: (idx: number, value: MortgageType) => void; }
  | { banks?: undefined; onMortgageTypeChange?: (idx: number, value: MortgageType) => void; }
);

const CaseCreationBasicInfoBanksSection = ({
  defaultValue, error, isOpen, onChange, banks, onMortgageTypeChange, defaultMortgageType,
}: Props) => {
  const { t } = useTranslation();
  const submittedBanksIds = banks?.filter((bank) => bank.isSubmitted).map((bank) => bank.id) ?? [];
  return (
    <Stack
      id='selected_banks'
      p='6'
      backgroundColor='utility-blue-50'
      borderRadius='radius-4'
      borderWidth='1px'
      borderColor='border-secondary'
      gap='6'
    >
      <Text weight='medium' size='3xl'>Banks</Text>
      <BankMultiSelect
        label={ t('clients.formLabels.banks') }
        maxBanks={ getMaxBankCount() }
        placeholder={ t('clients.formLabels.banks') }
        onValueChange={ (selectedBanks) => {
          onChange(selectedBanks.map((bank) => {
            const originalBank = defaultValue?.find((b) => b.id === bank.id);
            return {
              ...bank,
              mortgageType: originalBank?.mortgageType ?? defaultMortgageType,
            };
          }));
        } }
        isError={ !!error }
        error={ error }
        defaultValue={ defaultValue }
        isOpened={ isOpen }
        disabledBanks={ submittedBanksIds }
      />
      {!!banks?.length && (
        <SelectBankMortgageTypeCard
          banks={ banks }
          onMortgageTypeChange={ onMortgageTypeChange }
        />
      )}
    </Stack>
  );
};

export default CaseCreationBasicInfoBanksSection;
